import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";
import { CheckIcon } from "./icons";

/* ---------------------------------------------------------------------------
   The Organic kit, as React.

   Each of these wraps a class from the design system's stylesheet rather than
   restating its look in utilities, so a retune of the system lands here for
   free. Layout still belongs to the caller.
--------------------------------------------------------------------------- */

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export function Button({
  variant = "primary",
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button {...props} className={cn("btn", `btn-${variant}`, className)}>
      {children}
    </button>
  );
}

export function Card({
  className,
  children,
  ...props
}: {
  className?: string;
  children: ReactNode;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...props} className={cn("card", className)}>
      {children}
    </div>
  );
}

export function Tag({
  tone = "neutral",
  className,
  children,
}: {
  tone?: "accent" | "accent-2" | "neutral" | "outline";
  className?: string;
  children: ReactNode;
}) {
  return (
    <span className={cn("tag", `tag-${tone}`, className)}>{children}</span>
  );
}

/** Small uppercase section label — "today", "recent", "this week". */
export function Eyebrow({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return <div className={cn("eyebrow", className)}>{children}</div>;
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------

/** The bare streak number that sits at the end of a feed row. */
export function StreakCount({
  days,
  className,
}: {
  days: number;
  className?: string;
}) {
  return (
    <span
      className={cn("text-[13px] tabular-nums opacity-60", className)}
      title={days === 1 ? "1 period in a row" : `${days} periods in a row`}
    >
      {days}
    </span>
  );
}

/** The display-face streak, for the top of a goal or a profile. */
export function StreakBig({
  days,
  unit = "streak",
}: {
  days: number;
  unit?: string;
}) {
  return (
    <span className="big-num text-[28px]">
      {days}d <span className="font-body text-xs opacity-50">{unit}</span>
    </span>
  );
}

/** A filled check, the one moment of sage in a row. */
export function DoneMark() {
  return (
    <Tag tone="accent-2" className="px-2 py-1">
      <CheckIcon size={11} />
      <span className="sr-only">done</span>
    </Tag>
  );
}

/** The empty counterpart — an outline, never a warning. */
export function OpenMark() {
  return (
    <span
      className="size-[22px] shrink-0 rounded-full border-[1.5px] border-neutral-300"
      role="img"
      aria-label="not yet"
    />
  );
}

// ---------------------------------------------------------------------------

/**
 * Progress as "n of target" rather than a percentage: one pip per required
 * check-in, filled in sage. Weekly goals can be set as high as 50, at which
 * point pips stop being readable, so those fall back to a single bar.
 */
export function ProgressPips({
  value,
  target,
  size = "md",
  className,
}: {
  value: number;
  target: number;
  size?: "sm" | "md";
  className?: string;
}) {
  const height = size === "sm" ? "h-[7px]" : "h-[9px]";
  const label = `${value} of ${target}`;

  if (target > 12) {
    const pct = target > 0 ? Math.min(100, (value / target) * 100) : 0;
    return (
      <div
        className={cn(
          "w-full overflow-hidden rounded-full bg-neutral-200",
          height,
          className,
        )}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={target}
        aria-label={label}
      >
        <div
          className="h-full rounded-full bg-accent-2 transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    );
  }

  return (
    <div
      className={cn("flex gap-1", className)}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={target}
      aria-label={label}
    >
      {Array.from({ length: Math.max(1, target) }, (_, index) => (
        <span
          key={index}
          className={cn(
            "flex-1 rounded-full transition-colors duration-300",
            height,
            index < value ? "bg-accent-2" : "bg-neutral-200",
          )}
        />
      ))}
    </div>
  );
}

/**
 * A day in the history grid. "open" is drawn dashed and covers both ends of
 * the range that carry no verdict: days before the goal existed, and the
 * trailing run of days still waiting on a check-in.
 */
export type HistoryDay = "done" | "missed" | "open";

/** The last few weeks as a grid of squares, thirteen to a row. */
export function HistoryGrid({ days }: { days: HistoryDay[] }) {
  return (
    <div
      className="grid grid-cols-13 gap-1"
      role="img"
      aria-label={historyLabel(days)}
    >
      {days.map((day, index) => (
        <span
          key={index}
          className={cn(
            "aspect-square rounded-[4px]",
            day === "done" && "bg-accent-2",
            day === "missed" && "bg-neutral-200",
            day === "open" &&
              "border-[1.5px] border-dashed border-neutral-300 bg-neutral-100",
          )}
        />
      ))}
    </div>
  );
}

function historyLabel(days: HistoryDay[]): string {
  const done = days.filter((day) => day === "done").length;
  const counted = days.filter((day) => day !== "open").length;
  if (counted === 0) return "No history yet";
  return `${done} of the last ${counted} days`;
}

// ---------------------------------------------------------------------------

/** A display-face number over a small caption. Used in threes on a profile. */
export function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex-1 text-center">
      <div className="big-num text-[22px]">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.06em] opacity-50">
        {label}
      </div>
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn(
        "inline-block size-4 animate-spin rounded-full border-2 border-neutral-300 border-t-accent",
        className,
      )}
    />
  );
}

/**
 * Empty states here are a line of quiet text, sometimes with one way out.
 * No oversized emoji: the system asks for air, not decoration.
 */
export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3.5 px-5 py-14 text-center">
      <p className="text-[13px] opacity-55">{title}</p>
      {children && (
        <p className="max-w-[34ch] text-[13px] opacity-55">{children}</p>
      )}
      {action}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div className={cn("animate-pulse rounded-lg bg-neutral-200", className)} />
  );
}
